import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";

export default function UrgentPaymentSuccess() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [requestId, setRequestId] = useState(null);

  const pollStatus = useCallback(async (attempts = 0) => {
    if (!sessionId || !token || attempts >= 8) {
      if (attempts >= 8) setStatus("timeout");
      return;
    }

    try {
      const res = await axios.get(
        `${API}/payments/urgent-checkout-status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.payment_status === "paid") {
        setStatus("success");
        setRequestId(res.data.request_id);
        toast.success("Urgent request submitted successfully!");
        return;
      }

      if (res.data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Keep polling
      setTimeout(() => pollStatus(attempts + 1), 2000);
    } catch {
      if (attempts < 5) {
        setTimeout(() => pollStatus(attempts + 1), 2000);
      } else {
        setStatus("error");
      }
    }
  }, [sessionId, token]);

  useEffect(() => {
    pollStatus();
  }, [pollStatus]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center space-y-6">
          {status === "checking" && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
              <h2 className="text-xl font-semibold">Verifying payment...</h2>
              <p className="text-muted-foreground text-sm">Please wait while we confirm your $5 urgent request fee.</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
              <h2 className="text-xl font-semibold text-green-600">Payment Successful!</h2>
              <p className="text-muted-foreground text-sm">
                Your urgent request {requestId && <span className="font-medium">#{requestId}</span>} has been submitted.
                Nearby tyre shops will be notified immediately.
              </p>
              <Button onClick={() => navigate("/customer/dashboard")} className="w-full" data-testid="go-to-dashboard-btn">
                Go to Dashboard
              </Button>
            </>
          )}

          {(status === "error" || status === "timeout" || status === "expired") && (
            <>
              <XCircle className="h-12 w-12 text-destructive mx-auto" />
              <h2 className="text-xl font-semibold text-destructive">
                {status === "expired" ? "Session Expired" : "Payment Issue"}
              </h2>
              <p className="text-muted-foreground text-sm">
                {status === "expired"
                  ? "Your payment session has expired. Please try again."
                  : "We couldn't verify your payment. If you were charged, please contact support@tyrematch.co.nz."}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => navigate("/customer/quote-request")} className="flex-1">
                  Try Again
                </Button>
                <Button onClick={() => navigate("/customer/dashboard")} className="flex-1">
                  Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
